import { PlusOutlined } from '@ant-design/icons';
import { Col, Row, Typography } from 'antd';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { Card } from 'src/components/Card/Card';
import { DataView } from 'src/components/DataView/DataView';
import { Empty } from 'src/components/Empty/Empty';
import { ListPage } from 'src/components/FormPages/ListPage';
import { Link } from 'src/components/Link/Link';
import { ResourceNameView } from 'src/components/ResourceNameView/ResourceNameView';
import { EntityLinks } from 'src/core/router/EntityLinks';
import { Router } from 'src/core/router/Router';
import { PartnerDto } from 'src/generated-api-client';
import { useLoadResources } from 'src/hooks/useLoadResources';
import { PartnersStore } from 'src/stores/PartnersStore/PartnersStore';
import { PartnerTypeResourceStore } from 'src/stores/Resources/PartnerTypeResourceStore';
import { getDateFormattedValue } from 'src/utils/DateHelper';
import { Entity } from 'src/utils/mobx/BasicStore/BasicStore.types';

export const PartnersPage = observer(() => {
    useLoadResources([PartnerTypeResourceStore]);

    return (
        <ListPage
            pageName="Partners"
            store={PartnersStore}
            header={
                <Row gutter={16}>
                    <Col flex={1}>
                        <Typography.Title level={3}>
                            {t(
                                'App.Pages.Partners.Title',
                                'Partner management',
                            )}
                        </Typography.Title>
                    </Col>
                    <Col>
                        <Link
                            to={EntityLinks.partners.create?.()}
                            button
                            buttonType="primary"
                            icon={<PlusOutlined />}
                        >
                            {t(
                                'App.Pages.Partners.Button.Create',
                                'Create partner',
                            )}
                        </Link>
                    </Col>
                </Row>
            }
            empty={<Empty description={t('App.Pages.Partner.Empty')} />}
            renderItem={(item: Entity) => {
                const partner = item as unknown as PartnerDto;

                return (
                    <Card
                        key={partner.id}
                        onClick={() => {
                            const detailsLink =
                                partner.id !== undefined
                                    ? EntityLinks.partners.itemDetails?.(
                                          partner.id,
                                      )
                                    : undefined;
                            detailsLink && Router.navigate(detailsLink);
                        }}
                    >
                        <Row gutter={[16, 16]}>
                            <Col xs={12} sm={8} md={4}>
                                <DataView
                                    label={t('Partner.ID', 'Partner ID')}
                                    value={partner.number}
                                />
                            </Col>
                            <Col xs={12} sm={8} md={4}>
                                <DataView
                                    label={t(
                                        'Partner.ArabicName',
                                        'Arabic Name',
                                    )}
                                    value={partner.arabicName}
                                />
                            </Col>
                            <Col xs={12} sm={8} md={4}>
                                <DataView
                                    label={t(
                                        'Partner.EnglishName',
                                        'English Name',
                                    )}
                                    value={partner.englishName}
                                />
                            </Col>
                            <Col xs={12} sm={8} md={4}>
                                <ResourceNameView
                                    label={t('Partner.Type', 'Type')}
                                    value={partner?.partnerType}
                                    resourceStore={PartnerTypeResourceStore}
                                />
                            </Col>
                            <Col xs={12} sm={8} md={4}>
                                <DataView
                                    label={t(
                                        'Partner.CreatedAt',
                                        'Creation date',
                                    )}
                                    value={getDateFormattedValue(
                                        partner.createdAt ?? '',
                                        true,
                                    )}
                                />
                            </Col>
                            {partner.jahezComissionType === 'FIXED_AMOUNT' ? (
                                <Col xs={12} sm={8} md={4}>
                                    <DataView
                                        label={t('Partner.JahezCommission')}
                                        formatValue="currency-sar"
                                        value={partner.jahezComissionAmount} // todo check this
                                    />
                                </Col>
                            ) : (
                                <Col xs={12} sm={8} md={4}>
                                    <DataView
                                        label={t('Partner.JahezCommission')}
                                        formatValue="percent"
                                        value={partner.jahezComissionPct} // todo check this
                                    />
                                </Col>
                            )}
                        </Row>
                    </Card>
                );
            }}
        />
    );
});
